import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { forms as formsApi } from '@/utils/api';
import { Zap, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

function shouldShow(field, values, fieldsById) {
  const c = field.conditional;
  if (!c || !c.field_id) return true;
  const ref = values[c.field_id];
  if (c.op === 'truthy') return !!ref;
  if (c.op === 'equals') return String(ref || '') === String(c.value || '');
  if (c.op === 'not_equals') return String(ref || '') !== String(c.value || '');
  if (c.op === 'contains') return (ref || '').toString().toLowerCase().includes(String(c.value || '').toLowerCase());
  return true;
}

function FieldRenderer({ field, value, onChange }) {
  const common = {
    value: value || '',
    onChange: (e) => onChange(field.type === 'checkbox' ? e.target.checked : e.target.value),
    placeholder: field.placeholder || '',
    required: !!field.required,
    className: 'input-agency text-sm',
  };
  if (field.type === 'textarea') return <textarea {...common} rows={4} className="input-agency text-sm resize-none" />;
  if (field.type === 'select') {
    return (
      <select {...common}>
        <option value="">Select…</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'radio') {
    return (
      <div className="space-y-1">
        {(field.options || []).map(o => (
          <label key={o} className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input type="radio" name={field.id} value={o} checked={value === o} onChange={e => onChange(e.target.value)} required={field.required} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} required={field.required} />
        {field.placeholder || field.label}
      </label>
    );
  }
  return <input {...common} type={field.type || 'text'} />;
}

export default function IntakeForm() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    formsApi.public(slug).then(r => setData(r.data)).catch(() => setData('error'));
  }, [slug]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF8F4' }}><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} /></div>;
  if (data === 'error') return <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4' }}><p className="text-sm" style={{ color: '#5C6B72' }}>Form not found.</p></div>;

  const { form, brand } = data;
  const color = brand?.primary_color || '#006080';

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4' }}>
        <div className="max-w-md w-full text-center p-8" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          <CheckCircle2 size={48} style={{ color, margin: '0 auto 16px' }} />
          <h1 className="text-2xl font-black mb-2" style={{ color: '#1A2428' }}>Submitted</h1>
          <p className="text-sm" style={{ color: '#5C6B72' }}>{done}</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Only submit visible fields
      const fieldsById = Object.fromEntries(form.fields.map(f => [f.id, f]));
      const payload = {};
      form.fields.forEach(f => {
        if (shouldShow(f, values, fieldsById)) payload[f.id] = values[f.id];
      });
      const r = await formsApi.submit(slug, payload);
      setDone(r.data.message || "Thanks! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const fieldsById = Object.fromEntries(form.fields.map(f => [f.id, f]));

  return (
    <div className="min-h-screen px-4 py-12" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color }}>
            <Zap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-base" style={{ color: '#1A2428' }}>{brand?.name || 'Agency'}</span>
        </div>

        <h1 className="text-3xl font-black mb-2" style={{ color: '#1A2428' }} data-testid="intake-form-title">{form.title}</h1>
        {form.description && <p className="text-sm mb-6" style={{ color: '#5C6B72' }}>{form.description}</p>}

        <form onSubmit={handleSubmit} className="space-y-4 rounded-card p-6" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
          {form.fields.filter(f => shouldShow(f, values, fieldsById)).map(f => (
            <div key={f.id}>
              {f.type !== 'checkbox' && (
                <label className="block text-sm font-semibold mb-1" style={{ color: '#1A2428' }}>
                  {f.label} {f.required && <span style={{ color: '#B34A3A' }}>*</span>}
                </label>
              )}
              <FieldRenderer field={f} value={values[f.id]} onChange={(v) => setValues(vals => ({ ...vals, [f.id]: v }))} />
            </div>
          ))}
          <button type="submit" disabled={submitting} className="text-sm font-semibold w-full py-3 rounded-btn text-white transition-opacity disabled:opacity-60" style={{ background: color }} data-testid="intake-submit-button">
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}
